import {
  DecisionOverrideRationales,
  LoanStatus
} from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtIndividualLoanDetails/types';
import {
  FlyoutStatusLoanData,
  Loan,
  MasterPoolRemarkPayload,
  MasterPoolRemarkResponse,
  PoolProcessingStatusCode,
  PortfolioInsuranceType,
  RemarksDropDownHeader,
  ValidationRemarkPayload,
  ValidationRemarkResponse
} from '../types';
import { uniqueId } from 'lodash-es';
import { LoanInventoryTableFields } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtPortfolioLoansInventoryTable/types';
import { Remark } from '../../../types';
import { isRemarkOther } from 'Feature/V2/Shared_Portfolio/utils/remarkOther';
import {
  PoolPrecisionTypeCode,
  PortfolioLoanStatusCode
} from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/api/types';
import i18n from 'i18next';
import {
  dropdownIdsApprovedStatus,
  dropdownIdsWithdrawnStatus,
  REASON_FOR_FAILING_LOAN_CANCELLATION,
  REASON_FOR_FAILING_LOAN_NON_CANCELLATION,
  REASON_FOR_PASSING_LOAN_CANCELLATION,
  REASON_FOR_PASSING_LOAN_NON_CANCELLATION
} from 'Feature/V2/HbtMasterPool/constants';
import { LoanStatusCode } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementPage/types';

export type FlyoutType = 'updateLoan' | 'updateRemark' | 'changeStatus';
export type FlyoutStatus = '06' | '05' | '02' | '03' | null;

export enum ValidationReviewFlyoutStatus {
  FAILED = '03',
  PASSED = '02'
}
export interface ValidationRemarkLoan {
  id: string;
  accountNumber: string;
  validationReviewLoanData?: FlyoutStatusLoanData;
  status?: string;
  lastRemark?: string;
  hasError?: boolean;
  errorCode?: string;
}
export interface MastpoolLoan {
  id: string;
  accountNumber: string;
  status?: string;
  hasError?: boolean;
  lastRemark?: string;
  errorCode?: string;
}

export const comboboxRationale = (
  typeOfFlyout: FlyoutType,
  listOfRationale: DecisionOverrideRationales[],
  status: FlyoutStatus,
  pptCode?: string | null,
  typeOfPortfolioInsurance?: PortfolioInsuranceType,
  remarksDropDownHeader?: RemarksDropDownHeader[]
): DecisionOverrideRationales[] => {
  if (typeOfFlyout === 'changeStatus' && status === PortfolioLoanStatusCode.Approved)
    return getApprovedRationale(listOfRationale);

  if (typeOfFlyout === 'changeStatus' && status === PortfolioLoanStatusCode.Withdrawn)
    return getWithdrawnRationale(listOfRationale);

  if (typeOfFlyout === 'updateLoan' && pptCode) {
    if (pptCode === PoolProcessingStatusCode.CANCELLED) {
      if (status === LoanStatusCode.PASSED) {
        return getRationaleForPassingCancelledLoans(listOfRationale);
      } else {
        return getRationaleForFailingCancelledLoans(listOfRationale);
      }
    } else {
      if (status === LoanStatusCode.PASSED) {
        return getRationaleForPassingNonCancelledLoans(listOfRationale);
      } else {
        return getRationaleForFailingNonCancelledLoans(listOfRationale);
      }
    }
  }

  if (
    typeOfPortfolioInsurance === 'validationReview' &&
    typeOfFlyout === 'updateRemark' &&
    remarksDropDownHeader
  ) {
    const remarksIDs = remarksDropDownHeader.flatMap((header) => header.ids);
    return listOfRationale.filter((rationale) => remarksIDs.includes(rationale.id));
  }

  if (remarksDropDownHeader) {
    const remarksIDs = remarksDropDownHeader.flatMap((header) => header.ids);
    return listOfRationale.filter((rationale) => remarksIDs.includes(rationale.id));
  }

  return [];
};

export const getApprovedRationale = (listOfRationale: DecisionOverrideRationales[]) => {
  return listOfRationale.filter((decisionRationale) => {
    return dropdownIdsApprovedStatus.includes(decisionRationale.id);
  });
};

export const getWithdrawnRationale = (listOfRationale: DecisionOverrideRationales[]) => {
  return listOfRationale.filter((decisionRationale) => {
    return dropdownIdsWithdrawnStatus.includes(decisionRationale.id);
  });
};

export const getRationaleForPassingCancelledLoans = (
  listOfRationale: DecisionOverrideRationales[]
) => {
  return listOfRationale.filter((decisionRationale) => {
    return REASON_FOR_PASSING_LOAN_CANCELLATION.includes(decisionRationale.id);
  });
};

export const getRationaleForFailingCancelledLoans = (
  listOfRationale: DecisionOverrideRationales[]
) => {
  return listOfRationale.filter((decisionRationale) => {
    return REASON_FOR_FAILING_LOAN_CANCELLATION.includes(decisionRationale.id);
  });
};

export const getRationaleForPassingNonCancelledLoans = (
  listOfRationale: DecisionOverrideRationales[]
) => {
  return listOfRationale.filter((decisionRationale) => {
    return REASON_FOR_PASSING_LOAN_NON_CANCELLATION.includes(decisionRationale.id);
  });
};

export const getRationaleForFailingNonCancelledLoans = (
  listOfRationale: DecisionOverrideRationales[]
) => {
  return listOfRationale.filter((decisionRationale) => {
    return REASON_FOR_FAILING_LOAN_NON_CANCELLATION.includes(decisionRationale.id);
  });
};

const getValidationReviewLoanData = (data: ValidationRemarkResponse) => {
  if (data.cmhcAccountNumber) return data.cmhcAccountNumber;
  else return data.lenderReferenceNumber!;
};

const getAccountNumberForMasterPool = (data: MasterPoolRemarkResponse) => {
  if (typeof data.cmhcAccountNumber === 'string') return data.cmhcAccountNumber;
  return '';
};

const getRemarks = (data: ValidationRemarkResponse) => {
  if (i18n.language === 'en') {
    return data.latestRemark?.rationaleEnglish;
  }

  return data.latestRemark?.rationaleFrench;
};
const getRemarksForMasterPool = (data: MasterPoolRemarkResponse) => {
  return i18n.language === 'en'
    ? data.lastRemark?.rationaleEnglish
    : data.lastRemark?.rationaleFrench;
};

export const generateValidationRemarkLoan = (
  data: ValidationRemarkResponse[]
): ValidationRemarkLoan[] => {
  return data.map((loan: ValidationRemarkResponse) => ({
    id: uniqueId(),
    accountNumber: '',
    validationReviewLoanData: getValidationReviewLoanData(loan),
    lastRemark: getRemarks(loan) ?? '',
    status: loan.status ?? '',
    hasError: Boolean(loan.errorCode),
    errorCode: loan.errorCode ?? undefined
  }));
};

export const generateMasterpoolLoan = (data: MasterPoolRemarkResponse[]): MastpoolLoan[] => {
  return data.map((loan: MasterPoolRemarkResponse) => ({
    id: uniqueId(),
    accountNumber: getAccountNumberForMasterPool(loan),
    status: loan.portfolioLoanAccountStatus ?? '',
    lastRemark: getRemarksForMasterPool(loan) ?? '',
    hasError: Boolean(loan.errorCode),
    errorCode: loan.errorCode ?? undefined
  }));
};

export const generateValidationPayload = (
  loans: FlyoutStatusLoanData[],
  targetStatus: string | null,
  validateOnly: boolean,
  pptCode: string | null,
  applicationId: string | null,
  portfolioIds: Number[],
  loansData?: LoanInventoryTableFields[],
  remark?: Remark,
  largeFormFieldValue?: string
): ValidationRemarkPayload => {
  const payload: any = {
    validateOnly,
    targetStatus,
    poolprecitiontypecode: pptCode!,
    applicationId: Number(applicationId),
    portfolioIds:
      portfolioIds.length > 0 ? portfolioIds : loansData?.map((loans) => Number(loans.portfolioId))
  };

  if (remark) {
    ((payload.decisionOverrideRationaleId = remark?.remarkID ?? undefined),
      (payload.decisionOverrideRemarks = isRemarkOther(remark)
        ? largeFormFieldValue
        : remark.remarkMessage));
  }

  return pptCode === PoolProcessingStatusCode.CANCELLED
    ? { ...payload, cmhcAccountNumbers: loans }
    : {
        ...payload,
        lenderReferenceNumbers: loans
      };
};

export const generateMasterPoolPayload = (
  loans: string[],
  targetStatus: string | null,
  validateOnly: boolean,
  remark?: Remark,
  largeFormFieldValue?: string
): MasterPoolRemarkPayload => {
  const payload: any = {
    loans,
    targetStatus,
    validateOnly
  };

  if (remark) {
    payload.remark = {
      remarkId: remark?.remarkID,
      remarkText: isRemarkOther(remark) ? largeFormFieldValue : remark.remarkMessage
    };
  }

  return payload;
};
